import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ModelError,
  ResponseError,
  Vulnerability,
} from '@rtkwlf/mr-infra-risk';
import { useDebounce } from '@uidotdev/usehooks';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';

import { useVulnerabilityRisksExports } from '@apis/mr-infra-risk/mutations/use-vulnerability-risks-export';
import {
  type GetVulnerabilitiesParams,
  useVulnerabilities,
} from '@apis/mr-infra-risk/queries/use-vulnerabilities';
import { ActionDrawer } from '@packages/components/action-drawer/action-drawer';
import type { GridApiPro } from '@packages/fenrir/components';
import {
  initTogglableColumns,
  type GridRowParams,
  type GridRowSelectionModel,
} from '@packages/fenrir/components';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import { FormHeaderVulnerabilities } from '../../components/action-drawer-form/form-header-vulnerabilities';
import { UpdateRisksForm } from '../../components/action-drawer-form/update-risk-form';
import { UpdateRiskFormFields } from '../../components/action-drawer-form/update-risks-form-fields';
import { RiskManagementTable } from '../../components/risks-management-table/risk-management-table';
import { RiskManagementTableWrapper } from '../../components/risks-management-table/risk-management-table-wrapper';
import { ParseError } from '../../filter-management/error-handling';
import { useExportColumns } from '../../hooks/use-export-columns/use-export-columns';
import {
  DEBOUNCE_TIME,
  LIMIT,
} from '../../hooks/use-risks-management-filters/filter.constants';
import { useRisksManagementFilters } from '../../hooks/use-risks-management-filters/use-risks-management-filters';
import { useSortParams } from '../../hooks/use-sort-params/use-sort-params';
import { ErrorSnackbar } from '../all-risks/error-snackbar/error-snackbar';
import { getInitialSortState } from '../risks/grid-state/get-intial-sort-state';

import { useVulnerabilitiesExportColumnNames } from './use-vulnerabilities-export-column-names/use-vulnerabilities-export-column-names';
import { useVulnerabilitiesTableColumns } from './use-vulnerabilities-table-columns/use-vulnerabilities-table-columns';
import {
  DEFAULT_FILTER,
  DEFAULT_SORT,
  DEFAULT_VULNERABILITY_COLUMN_MODEL,
  VULNERABILITY_COLUMN_DEFS,
  VULNERABILITY_SUPPORTED_SORT_FIELDS,
  EXPORT_VULNERABILITY_API_NAMES,
  vulnQueryParamsMap,
} from './vuln-table.constants';
import { VulnerabilitiesFilterMap } from './vulnerabilities-filter-map';
import { VulnerabilitiesRowExpansion } from './vulnerabilities-row-expansion/vulnerabilities-row-expansion';

export const VULNERABILITY_GRID_STATE_KEY = 'vulnerabilityDataGridState';

const hiddenColumns = initTogglableColumns([
  VULNERABILITY_COLUMN_DEFS.ISSUE_ID,
]);

const VulnerabilitiesPage = () => {
  const columns = useVulnerabilitiesTableColumns();
  const gridApiRef = useRef<GridApiPro | null>(null);

  const getVulnerabilitiesDetailPanelContent = useCallback(
    ({ row }: GridRowParams<Vulnerability>) => (
      <VulnerabilitiesRowExpansion row={row} />
    ),
    [],
  );

  const { filterModel, setFilterModel, riskParams } =
    useRisksManagementFilters<GetVulnerabilitiesParams>(
      vulnQueryParamsMap,
      DEFAULT_FILTER,
      {
        gridStateKey: VULNERABILITY_GRID_STATE_KEY,
        filtersMap: VulnerabilitiesFilterMap,
      },
    );

  const { sortModel, setSortModel, sortParams } = useSortParams({
    defaultSort: getInitialSortState(
      VULNERABILITY_GRID_STATE_KEY,
      DEFAULT_SORT,
    ),
    supportedSortFields: VULNERABILITY_SUPPORTED_SORT_FIELDS,
  });

  const combineVulnerabilityParams = useMemo(() => {
    return { ...riskParams, ...sortParams, limit: LIMIT };
  }, [riskParams, sortParams]);

  const debouncedVulnParams = useDebounce(
    combineVulnerabilityParams,
    DEBOUNCE_TIME,
  );

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const {
    data: vulnResponse,
    error,
    isPending,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
  } = useVulnerabilities(debouncedVulnParams);

  const [ffM2] = useFeatureFlag('RISKS_IN_UP_M2');
  const [errMessage, setErrMessage] = useState('');

  const { t } = useTranslation('riskManagement', {
    keyPrefix: 'risks',
  });

  const { t: tVuln } = useTranslation('riskManagement', {
    keyPrefix: 'vulnerabilities',
  });

  const handleOnRowsScrollEnd = useCallback(() => {
    if (
      !isFetchingNextPage &&
      (vulnResponse?.pages
        ? vulnResponse.pages[vulnResponse?.pages.length - 1].data.length ===
          LIMIT
        : true)
    )
      fetchNextPage();
  }, [isFetchingNextPage, vulnResponse?.pages, fetchNextPage]);

  const handleErrorMessage = useCallback(
    async (error: ResponseError, defaultError: string) => {
      const errorBody: ModelError[] = await error.response.json();
      setErrMessage(ParseError(errorBody, defaultError));
    },
    [],
  );

  const boxRef = useRef<HTMLDivElement | null>(null);

  const { mutateAsync: startExport } = useVulnerabilityRisksExports({
    onSuccess: (data) => {
      window.open(data.downloadURL);
    },
  });

  useEffect(() => {
    if (isError) {
      handleErrorMessage(error, t('notifications.error'));
    } else {
      setErrMessage('');
    }
  }, [error, handleErrorMessage, isError, t]);

  const updateDrawerComponent = useMemo(() => {
    const newSelectedRows =
      vulnResponse?.pages
        .flatMap((page) => page.data)
        .filter((r) => rowSelectionModel.includes(r.issueID)) || [];

    return (
      <UpdateRisksForm
        drawerData={newSelectedRows.map((r) => {
          return {
            id: r.issueID,
            state: '',
            assignee: '',
            deadline: null,
          };
        })}
        setRowSelectionModel={setRowSelectionModel}
        mutate={noop} // Placeholder for the mutate function
        updateStatus="success" // Mocking the status for demonstration
        actionTitle={tVuln('actionDrawerTitle')}
      >
        <FormHeaderVulnerabilities
          rowsSelected={newSelectedRows.length}
          totalRiskCountUnresolved={newSelectedRows.reduce(
            (sum, item) => sum + item.riskCountUnresolved,
            0,
          )}
        />
        <UpdateRiskFormFields />
      </UpdateRisksForm>
    );
  }, [rowSelectionModel, tVuln, vulnResponse?.pages]);

  const { EXPORT_VULNERABILITY_COLUMN_NAMES } =
    useVulnerabilitiesExportColumnNames();

  const { getExportColumns } = useExportColumns({
    gridApiRef,
    splApiNames: EXPORT_VULNERABILITY_API_NAMES,
    columnNamesMap: EXPORT_VULNERABILITY_COLUMN_NAMES,
    allColDefs: VULNERABILITY_COLUMN_DEFS,
  });

  const handleExport = useCallback(
    (isCurrentView: boolean) => {
      const columnNames = isCurrentView
        ? getExportColumns()
        : EXPORT_VULNERABILITY_COLUMN_NAMES;

      const ids = rowSelectionModel.map((id) => String(id));

      startExport({
        queryString: riskParams,
        columnNames,
        ids,
      });
    },
    [
      EXPORT_VULNERABILITY_COLUMN_NAMES,
      getExportColumns,
      riskParams,
      rowSelectionModel,
      startExport,
    ],
  );

  const exportModel = useMemo(() => {
    return ffM2
      ? {
          onExportAll: () => handleExport(false),
          onExportFiltered: () => handleExport(true),
        }
      : undefined;
  }, [ffM2, handleExport]);

  return (
    <RiskManagementTableWrapper boxRef={boxRef}>
      <RiskManagementTable<Vulnerability>
        rows={vulnResponse?.pages.flatMap((page) => page.data) ?? []}
        columns={columns}
        columnVisibilityModel={DEFAULT_VULNERABILITY_COLUMN_MODEL}
        rowExpansionContent={getVulnerabilitiesDetailPanelContent}
        isLoading={isPending || isFetching || isFetchingNextPage}
        handleOnRowsScrollEnd={handleOnRowsScrollEnd}
        gridStateKey={VULNERABILITY_GRID_STATE_KEY}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        rowSelectionModel={rowSelectionModel}
        setRowSelectionModel={setRowSelectionModel}
        getHiddenColumns={hiddenColumns}
        updateDrawerComponent={() => updateDrawerComponent}
        getRowId={(row: Vulnerability) => row.issueID}
        gridApiRef={gridApiRef}
        exportModel={exportModel}
      />
      <ActionDrawer container={boxRef.current} />
      <ErrorSnackbar isError={isError} message={errMessage} />
    </RiskManagementTableWrapper>
  );
};

export default VulnerabilitiesPage;

export const Component = () => <VulnerabilitiesPage />;
